import React, { useEffect, useState } from "react";
import { Inbox, Sparkles, ExternalLink, RefreshCw, Trash2, MessageSquare } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

function StatusBadge({ status }) {
    const map = {
        received: { label: "Ricevuto", c: "var(--muted)" },
        processed: { label: "Analizzato", c: "var(--cobalt)" },
        job_created: { label: "Commessa creata", c: "var(--green)" },
        unauthorized: { label: "Non autorizzato", c: "var(--red)" },
    };
    const v = map[status] || { label: status, c: "var(--muted)" };
    return (
        <span className="mono text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded" style={{ background: "var(--bg)", color: v.c, border: `1px solid ${v.c}40` }}>
            {v.label}
        </span>
    );
}

export default function InboxPage() {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/inbox");
            setMessages(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const del = async (id) => {
        if (!window.confirm("Eliminare il messaggio dall'inbox?")) return;
        await api.delete(`/inbox/${id}`);
        load();
    };

    return (
        <div className="space-y-6" data-testid="inbox-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <h1
                        className="tracking-tight"
                        style={{
                            color: "var(--navy)",
                            fontFamily: "Geist, Outfit, sans-serif",
                            fontSize: "clamp(1.7rem, 2.8vw, 2.2rem)",
                            fontWeight: 200,
                            letterSpacing: "-0.035em",
                            lineHeight: 1.05,
                        }}
                    >
                        Inbox <span style={{ fontWeight: 600 }}>Telegram</span>
                    </h1>
                    <p className="text-[13px] mt-1" style={{ color: "var(--text-2)" }}>
                        Messaggi inoltrati al bot dai tuoi operatori e processati dall'AI · {messages.length} totali
                    </p>
                </div>
                <button data-testid="inbox-refresh" onClick={load} className="px-4 py-2.5 rounded-lg text-[13px] font-bold flex items-center gap-2"
                    style={{ background: "var(--surface)", border: "1px solid var(--line-2)", color: "var(--text)" }}>
                    <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Aggiorna
                </button>
            </div>

            {messages.length === 0 ? (
                <div className="card-tpl p-12 text-center">
                    <Inbox size={28} className="mx-auto mb-3" style={{ color: "var(--muted-2)" }} />
                    <div className="font-bold text-[15px]" style={{ color: "var(--navy)" }}>Nessun messaggio ricevuto</div>
                    <p className="text-[13px] mt-2 max-w-md mx-auto" style={{ color: "var(--text-2)" }}>
                        Quando i tuoi operatori inoltreranno messaggi cliente al bot Telegram, appariranno qui. Configura il bot in <b style={{ color: "var(--mod-accent)" }}>Configurazione → Bot Telegram</b>.
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {messages.map((m) => (
                        <div key={m.id} data-testid={`inbox-${m.id}`} className="card-tpl p-5">
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-lg grid place-items-center flex-shrink-0" style={{ background: "var(--mod-accent-soft)" }}>
                                    <MessageSquare size={18} style={{ color: "var(--mod-accent)" }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                        <span className="font-bold text-[14px]" style={{ color: "var(--navy)" }}>{m.from_name || "Telegram user"}</span>
                                        {m.from_username && <span className="mono text-[11px]" style={{ color: "var(--muted)" }}>@{m.from_username}</span>}
                                        <StatusBadge status={m.status} />
                                        <span className="mono text-[10px] ml-auto" style={{ color: "var(--muted-2)" }}>
                                            {new Date(m.received_at).toLocaleString("it-IT")}
                                        </span>
                                    </div>
                                    <div className="text-[13px] mb-3 p-3 rounded-md" style={{ background: "var(--bg)", color: "var(--text-2)", whiteSpace: "pre-wrap" }}>
                                        {m.raw_text || "(nessun testo)"}
                                    </div>

                                    {m.ai_result && (
                                        <div className="rounded-md p-3 mb-3" style={{ background: "var(--cobalt-soft)" }}>
                                            <div className="flex items-center gap-2 mb-2">
                                                <Sparkles size={12} style={{ color: "var(--cobalt)" }} />
                                                <span className="mono text-[10px] uppercase tracking-wider font-bold" style={{ color: "var(--cobalt)" }}>Analisi AI</span>
                                                <span className="mono text-[10px]" style={{ color: "var(--muted)" }}>· confidenza {Math.round((m.ai_result.confidence || 0) * 100)}%</span>
                                            </div>
                                            <div className="text-[12.5px] italic mb-2" style={{ color: "var(--navy)" }}>"{m.ai_result.summary}"</div>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11.5px]">
                                                {m.ai_result.customer_name && <div><span className="mono text-[9px] uppercase tracking-wider" style={{ color: "var(--muted)" }}>Cliente:</span> <b>{m.ai_result.customer_name}</b></div>}
                                                {m.ai_result.department && <div><span className="mono text-[9px] uppercase tracking-wider" style={{ color: "var(--muted)" }}>Reparto:</span> <b>{m.ai_result.department}</b></div>}
                                                {m.ai_result.quantity != null && <div><span className="mono text-[9px] uppercase tracking-wider" style={{ color: "var(--muted)" }}>Qty:</span> <b>{m.ai_result.quantity}</b></div>}
                                                {m.ai_result.deadline && <div><span className="mono text-[9px] uppercase tracking-wider" style={{ color: "var(--muted)" }}>Scadenza:</span> <b>{m.ai_result.deadline}</b></div>}
                                                {m.ai_result.priority && <div><span className="mono text-[9px] uppercase tracking-wider" style={{ color: "var(--muted)" }}>Priorità:</span> <b style={{ color: m.ai_result.priority === "urgent" ? "var(--mod-accent)" : "var(--text)" }}>{m.ai_result.priority}</b></div>}
                                            </div>
                                        </div>
                                    )}

                                    {m.attachments?.length > 0 && (
                                        <div className="flex gap-1.5 mb-2 flex-wrap">
                                            {m.attachments.map((a, i) => (
                                                <span key={i} className="mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-bold" style={{ background: "var(--mod-accent-soft)", color: "var(--orange)" }}>
                                                    📎 {a}
                                                </span>
                                            ))}
                                        </div>
                                    )}

                                    <div className="flex items-center gap-3 pt-2" style={{ borderTop: "1px dashed var(--line)" }}>
                                        {m.job && (
                                            <a href="/commesse" className="inline-flex items-center gap-1 text-[12px] font-bold hover:underline" style={{ color: "var(--green)" }}>
                                                <ExternalLink size={11} /> Commessa #{m.job.job_number}
                                            </a>
                                        )}
                                        <button onClick={() => del(m.id)} className="text-[11px] ml-auto" style={{ color: "var(--red)" }}>
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
